import { useEffect, useRef } from 'react';
import { Outlet, useLocation } from 'react-router-dom';
import { SidebarInset, SidebarProvider, TooltipProvider } from '@proerp/ui';
import { CommandPalette } from '@/features/search/CommandPalette';
import { useUiStore } from '@/store/uiStore';
import { AppSidebar } from './AppSidebar';
import { ChunkReloadReset } from './RouteErrorBoundary';
import { BottomNav } from './BottomNav';
import { FloatingTimer } from './FloatingTimer';
import { ShortcutsDialog } from './ShortcutsDialog';
import { Topbar } from './Topbar';

/** Tippt der Nutzer gerade in ein Eingabefeld? (Dann kein „?"-Shortcut.) */
function isTypingTarget(target: EventTarget | null): boolean {
  const el = target as HTMLElement | null;
  if (!el) return false;
  return el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.tagName === 'SELECT' || el.isContentEditable;
}

/**
 * Start-Zustand der Sidebar: das shadcn-Cookie (`sidebar_state`) wird zwar beim
 * Umschalten geschrieben, aber nie gelesen (Next.js-Vorlage liest es serverseitig
 * — in dieser Vite-SPA fehlt dieser Schritt bisher). Ohne Override startet die
 * Sidebar IMMER ausgeklappt, auch nach einem manuellen Einklappen + Reload.
 * Respektiert also zuerst eine explizite Nutzer-Entscheidung; ohne Cookie klappt
 * sie auf schmalen Tablet-Breiten (Hochformat-iPad, < `lg`) automatisch ein —
 * sonst bleibt für Suche/Filter/Toolbar-Buttons in Listen kaum noch Platz (§Mobile).
 */
export function getDefaultSidebarOpen(): boolean {
  const cookieMatch = document.cookie.match(/(?:^|; )sidebar_state=(true|false)/);
  if (cookieMatch) return cookieMatch[1] === 'true';
  return window.innerWidth >= 1024;
}

/**
 * Grundlayout: Sidebar links (Desktop, shadcn-Sidebar mit Icon-Collapse),
 * Topbar oben, Content rechts (§5.1). Mobil ersetzt eine Bottom-Tab-Bar die
 * Sidebar (§5.2); der Content bekommt unten Platz, damit nichts hinter der
 * Bar verschwindet. Der Collapse-Zustand überlebt Reloads (Cookie des
 * SidebarProviders); Strg/⌘+B klappt um.
 */
/** Scrollposition je Verlaufseintrag (location.key) — überlebt Navigation, nicht Reload. */
const scrollPositions = new Map<string, number>();

/**
 * Stellt die Scrollposition des Content-Containers je History-Eintrag wieder her.
 * Beim Vorwärts-Navigieren (neuer key) → oben; beim Zurück (bekannter key) → an
 * die gemerkte Stelle. Mehrere rAF-Versuche, da die Liste ihre Daten erst async
 * rendert (aus dem Query-Cache aber meist sofort).
 */
function useScrollRestoration(ref: React.RefObject<HTMLDivElement>) {
  const { key } = useLocation();
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const target = scrollPositions.get(key) ?? 0;
    let frame = 0;
    let tries = 0;
    const apply = () => {
      if (!ref.current) return;
      ref.current.scrollTop = target;
      tries += 1;
      // Inhalt evtl. noch nicht hoch genug → erneut versuchen (bis ~10 Frames).
      if (tries < 10 && Math.abs(ref.current.scrollTop - target) > 1) frame = requestAnimationFrame(apply);
    };
    frame = requestAnimationFrame(apply);
    const onScroll = () => scrollPositions.set(key, el.scrollTop);
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => {
      cancelAnimationFrame(frame);
      el.removeEventListener('scroll', onScroll);
    };
  }, [key, ref]);
}

export function AppShell() {
  const isSearchOpen = useUiStore((state) => state.isSearchOpen);
  const setSearchOpen = useUiStore((state) => state.setSearchOpen);
  const isShortcutsOpen = useUiStore((state) => state.isShortcutsOpen);
  const setShortcutsOpen = useUiStore((state) => state.setShortcutsOpen);
  const scrollRef = useRef<HTMLDivElement>(null);
  useScrollRestoration(scrollRef);

  // Globale Tastenkürzel: ⌘K/Strg+K öffnet Suche & Befehle (ADR 0015), „?" den
  // Kürzel-Spickzettel (nur außerhalb von Eingabefeldern).
  useEffect(() => {
    function onKeyDown(event: KeyboardEvent) {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 'k') {
        event.preventDefault();
        setSearchOpen(true);
        return;
      }
      if (
        event.key === '?' &&
        !event.metaKey &&
        !event.ctrlKey &&
        !event.altKey &&
        !isTypingTarget(event.target)
      ) {
        event.preventDefault();
        setShortcutsOpen(true);
      }
    }
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [setSearchOpen, setShortcutsOpen]);

  return (
    <TooltipProvider>
      <ChunkReloadReset />
      <SidebarProvider
        defaultOpen={getDefaultSidebarOpen()}
        className="h-svh overflow-hidden"
        style={{ '--sidebar-width': '15rem' } as React.CSSProperties}
      >
        <AppSidebar />
        <SidebarInset className="overflow-hidden">
          <Topbar />
          <div ref={scrollRef} className="flex-1 overflow-y-auto bg-background pb-[calc(4rem+env(safe-area-inset-bottom))] tablet:pb-0">
            <Outlet />
          </div>
        </SidebarInset>
        <BottomNav />
        {/* Schwebender Timer — außerhalb des Outlets, damit er Routenwechsel überlebt (auf jeder Seite sichtbar). */}
        <FloatingTimer />
        <CommandPalette open={isSearchOpen} onClose={() => setSearchOpen(false)} />
        <ShortcutsDialog open={isShortcutsOpen} onOpenChange={setShortcutsOpen} />
      </SidebarProvider>
    </TooltipProvider>
  );
}

/** Einheitlicher Page-Header: Titel links, Aktionen rechts (§5.1 / §9.3). */
export function PageHeader({
  title,
  subtitle,
  actions,
}: {
  title: string;
  subtitle?: string;
  actions?: React.ReactNode;
}) {
  return (
    // Mobil: Titel über den Aktionen gestapelt; ab sm nebeneinander (Desktop-first unverändert).
    <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between sm:gap-4">
      <div>
        <h1 className="text-2xl font-semibold tracking-tight text-foreground">{title}</h1>
        {subtitle && <p className="mt-1 text-sm text-muted-foreground">{subtitle}</p>}
      </div>
      {actions && <div className="flex shrink-0 flex-wrap items-center gap-2">{actions}</div>}
    </div>
  );
}

/** Standard-Seitencontainer — Rhythmus wie shadcn dashboard-01 (gap/padding 16/24 px). */
export function Page({ children }: { children: React.ReactNode }) {
  return <div className="flex flex-col gap-4 p-4 md:gap-6 md:p-6">{children}</div>;
}
